import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { Camera, Trash2, Send, Bell, BellOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SPACE_COLORS } from "@/lib/constants";
import { UserAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMEZONES = ["Europe/Madrid", "Europe/London", "America/Mexico_City", "America/Bogota", "America/Buenos_Aires", "America/New_York", "UTC"];

const NOTIF_CATEGORIES = [
  { key: "tasks", label: "Tareas", desc: "Asignadas a ti, cerradas o vencidas" },
  { key: "chat", label: "Chat y menciones", desc: "Mensajes directos y menciones" },
  { key: "events", label: "Eventos", desc: "Nuevos eventos y cambios de agenda" },
  { key: "expenses", label: "Gastos y presupuestos", desc: "Gastos compartidos y alertas" },
  { key: "polls", label: "Encuestas", desc: "Nuevas encuestas y resultados" },
  { key: "other", label: "Otras", desc: "Automatizaciones y avisos generales" },
];

const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const NotificationsCard = ({ user, setUser }) => {
  const qc = useQueryClient();
  const [cats, setCats] = useState(() => {
    const c = user?.notify_prefs?.categories || {};
    return Object.fromEntries(NOTIF_CATEGORIES.map(({ key }) => [key, c[key] !== false]));
  });
  const [savingCats, setSavingCats] = useState(false);
  const [digest, setDigest] = useState(!!user?.notify_prefs?.daily_digest);
  const [linking, setLinking] = useState(false);
  const [pushSub, setPushSub] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [testingTg, setTestingTg] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [testingDigest, setTestingDigest] = useState(false);

  const { data: tg } = useQuery({
    queryKey: ["telegram-status"],
    queryFn: async () => (await api.get("/telegram/status")).data,
    refetchInterval: linking ? 3000 : false,
  });
  const { data: pushCfg } = useQuery({
    queryKey: ["push-config"],
    queryFn: async () => (await api.get("/push/public-key")).data,
  });

  useEffect(() => {
    if (tg?.linked && linking) {
      setLinking(false);
      toast.success(`Telegram vinculado${tg.telegram_name ? ` como ${tg.telegram_name}` : ""} 🎉`);
    }
  }, [tg?.linked, linking, tg?.telegram_name]);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      reg?.pushManager.getSubscription().then(setPushSub);
    });
  }, []);

  const savePrefs = async (nextCats, nextDigest) => {
    setSavingCats(true);
    try {
      const { data } = await api.put("/profile", { notify_prefs: { categories: nextCats, daily_digest: nextDigest } });
      setUser(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCats(false);
    }
  };
  const toggleCat = (key, v) => {
    const next = { ...cats, [key]: v };
    setCats(next);
    savePrefs(next, digest);
  };
  const toggleDigest = (v) => {
    setDigest(v);
    savePrefs(cats, v);
    if (v) toast.success("Resumen diario activado: cada mañana a las 8:00 en tu Telegram");
  };

  const linkTelegram = async () => {
    try {
      const { data } = await api.post("/telegram/link");
      window.open(data.deep_link, "_blank");
      setLinking(true);
      toast.info("Pulsa «Iniciar» en Telegram para completar la vinculación");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const unlinkTelegram = async () => {
    try {
      await api.post("/telegram/unlink");
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
      toast.success("Telegram desvinculado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const testTelegram = async () => {
    setTestingTg(true);
    try {
      await api.post("/telegram/test");
      toast.success("Mensaje enviado. Revisa tu Telegram 📱");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTestingTg(false);
    }
  };

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return toast.error("Este navegador no soporta notificaciones push");
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permiso de notificaciones denegado en el navegador");
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pushCfg.public_key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setPushSub(sub);
      toast.success("Notificaciones activadas en este dispositivo 🔔");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPushBusy(false);
    }
  };
  const disablePush = async () => {
    setPushBusy(true);
    try {
      if (pushSub) {
        await api.post("/push/unsubscribe", { endpoint: pushSub.endpoint });
        await pushSub.unsubscribe();
        setPushSub(null);
      }
      toast.success("Notificaciones desactivadas en este dispositivo");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPushBusy(false);
    }
  };
  const testPush = async () => {
    setTestingPush(true);
    try {
      await api.post("/push/test");
      toast.success("Notificación de prueba enviada");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTestingPush(false);
    }
  };

  return (
    <Card className="p-6 space-y-5" data-testid="profile-notifications-card">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
          <Bell className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-heading font-semibold">Notificaciones en el móvil</h2>
          <p className="text-xs text-muted-foreground">Recibe fuera de la app lo que hoy te llega por la campana</p>
        </div>
      </div>

      {/* Telegram */}
      <div className="rounded-2xl bg-secondary/50 p-4 space-y-3" data-testid="telegram-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[#229ED9]"><TelegramIcon /></span>
            <div>
              <p className="text-sm font-semibold">Telegram</p>
              <p className="text-[11px] text-muted-foreground" data-testid="telegram-status-text">
                {tg?.configured === false
                  ? "Pendiente de configurar en el servidor"
                  : tg?.linked
                  ? `Vinculado${tg.telegram_name ? ` como ${tg.telegram_name}` : ""}`
                  : linking ? "Esperando confirmación en Telegram…" : "Sin vincular"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {tg?.linked ? (
              <>
                <Button variant="outline" size="sm" onClick={testTelegram} disabled={testingTg} data-testid="telegram-test-btn">
                  <Send className="h-3.5 w-3.5 mr-1" /> {testingTg ? "Enviando…" : "Probar"}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={unlinkTelegram} data-testid="telegram-unlink-btn">
                  Desvincular
                </Button>
              </>
            ) : (
              <Button size="sm" variant={tg?.configured ? "default" : "outline"} onClick={linkTelegram} disabled={!tg?.configured} data-testid="telegram-link-btn">
                Vincular Telegram
              </Button>
            )}
          </div>
        </div>
        {tg?.linked && (
          <div className="flex items-center justify-between gap-3 pt-1 border-t border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium">Resumen diario por Telegram</p>
              <p className="text-[11px] text-muted-foreground">Cada mañana a las 8:00: tus tareas del día y tu agenda</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="digest-send-now-btn"
                disabled={testingDigest}
                onClick={async () => {
                  setTestingDigest(true);
                  try {
                    await api.post("/telegram/digest-test");
                    toast.success("Resumen enviado a tu Telegram 📱");
                  } catch (e) {
                    toast.error(apiError(e));
                  } finally {
                    setTestingDigest(false);
                  }
                }}>
                <Send className="h-3.5 w-3.5 mr-1" /> {testingDigest ? "Enviando…" : "Enviar ahora"}
              </Button>
              <Switch data-testid="digest-switch" checked={digest} onCheckedChange={toggleDigest} />
            </div>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-secondary/50 p-4 space-y-3" data-testid="push-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <div>
              <p className="text-sm font-semibold">Notificaciones del navegador</p>
              <p className="text-[11px] text-muted-foreground" data-testid="push-status-text">
                {pushSub ? "Activas en este dispositivo" : "Desactivadas en este dispositivo"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {pushSub ? (
              <>
                <Button variant="outline" size="sm" onClick={testPush} disabled={testingPush} data-testid="push-test-btn">
                  <Send className="h-3.5 w-3.5 mr-1" /> {testingPush ? "Enviando…" : "Probar"}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={disablePush} disabled={pushBusy} data-testid="push-disable-btn">
                  <BellOff className="h-3.5 w-3.5 mr-1" /> Desactivar
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={enablePush} disabled={pushBusy || !pushCfg?.configured} data-testid="push-enable-btn">
                {pushBusy ? "Activando…" : "Activar en este dispositivo"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Categorías compartidas */}
      <div className="space-y-3" data-testid="notif-categories">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">
          Qué quieres recibir {savingCats && <span className="normal-case font-normal">· guardando…</span>}
        </p>
        {NOTIF_CATEGORIES.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{label}</p>
              <p className="text-[11px] text-muted-foreground truncate">{desc}</p>
            </div>
            <Switch data-testid={`notif-cat-${key}`} checked={cats[key]} onCheckedChange={(v) => toggleCat(key, v)} />
          </div>
        ))}
      </div>
    </Card>
  );
};

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [avatarColor, setAvatarColor] = useState(user?.avatar_color || SPACE_COLORS[0]);
  const [tz, setTz] = useState(user?.timezone || "Europe/Madrid");
  const [saving, setSaving] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("La imagen no puede superar 5 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/avatar", fd);
      setUser(data);
      toast.success("Foto de perfil actualizada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    try {
      const { data } = await api.delete("/profile/avatar");
      setUser(data);
      toast.success("Foto eliminada");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name, avatar_color: avatarColor, timezone: tz, theme });
      setUser(data);
      toast.success("Perfil actualizado");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Contraseña actualizada");
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-6" data-testid="profile-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mi perfil</h1>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <UserAvatar name={name} color={avatarColor} src={user?.avatar_url} size="lg" />
            <button
              data-testid="profile-avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title="Subir foto"
              className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:scale-110 transition-transform"
            >
              <Camera className="h-3.5 w-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-input" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-semibold text-lg">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            {user?.avatar_url && (
              <button data-testid="profile-avatar-remove-btn" onClick={removeAvatar}
                className="text-xs text-destructive flex items-center gap-1 mt-1 hover:underline">
                <Trash2 className="h-3 w-3" /> Quitar foto
              </button>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-name">Nombre</Label>
          <Input id="profile-name" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </div>
        <div className="space-y-2">
          <Label>Color de avatar</Label>
          <div className="flex flex-wrap gap-2">
            {SPACE_COLORS.map((c) => (
              <button key={c} data-testid={`profile-color-${c.replace("#", "")}`} onClick={() => setAvatarColor(c)}
                className={`h-8 w-8 rounded-full transition-transform ${avatarColor === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Zona horaria</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="profile-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>Modo oscuro</Label>
            <p className="text-xs text-muted-foreground">Cambia el aspecto de toda la aplicación</p>
          </div>
          <Switch data-testid="profile-dark-mode-switch" checked={theme === "dark"}
            onCheckedChange={(v) => setTheme(v ? "dark" : "light")} />
        </div>
        <Button data-testid="profile-save-btn" onClick={saveProfile} disabled={saving}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </Card>

      <NotificationsCard user={user} setUser={setUser} />

      <Card className="p-6 space-y-4">
        <h2 className="font-heading font-semibold">Cambiar contraseña</h2>
        <form onSubmit={changePassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-pw">Contraseña actual</Label>
            <Input id="current-pw" data-testid="profile-current-password-input" type="password" value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pw">Nueva contraseña</Label>
            <Input id="new-pw" data-testid="profile-new-password-input" type="password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)} required minLength={6} placeholder="Mínimo 6 caracteres" />
          </div>
          <Button type="submit" variant="outline" data-testid="profile-change-password-btn" disabled={changingPw}>
            {changingPw ? "Actualizando..." : "Actualizar contraseña"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 flex items-center justify-between">
        <div>
          <p className="font-medium text-sm">Cerrar sesión</p>
          <p className="text-xs text-muted-foreground">Saldrás de tu cuenta en este dispositivo</p>
        </div>
        <Button variant="destructive" data-testid="profile-logout-btn"
          onClick={async () => { await logout(); navigate("/auth"); }}>
          Cerrar sesión
        </Button>
      </Card>
    </div>
  );
}
